/**
 * MarketingCampaignsPage — Phase 32.9.
 *
 * Full list of all marketing campaigns with status filter, product search,
 * date range filter, and column-click sort.
 */

import { useState, useMemo } from 'react';
import { Navigate, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { isOmegaAdmin } from '@/features/omega/adminMode';
import { Breadcrumbs } from '@/features/omega/Breadcrumbs';
import { apiFetch, apiBaseUrl } from '@/api/http';
import { signHashWithFounderKey } from '@/api/real/founderSign';
import { getAccessToken } from '@/api/supabase';

// ── E3 / QC-003: founder bulk-reject by product slug ──────────────────────────

function sortKeys(obj: unknown): unknown {
  if (Array.isArray(obj)) return obj.map(sortKeys);
  if (obj !== null && typeof obj === 'object') {
    return Object.fromEntries(
      Object.entries(obj as Record<string, unknown>)
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([k, v]) => [k, sortKeys(v)]),
    );
  }
  return obj;
}

async function sha256hex(text: string): Promise<string> {
  const buf = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(text));
  return Array.from(new Uint8Array(buf)).map(b => b.toString(16).padStart(2, '0')).join('');
}

/** Founder-signed bulk-reject of every approved campaign for `slug`. Returns the count. */
async function bulkRejectBySlug(slug: string): Promise<number> {
  const body = { nonce: crypto.randomUUID(), slug };
  const canonical = JSON.stringify(sortKeys(body));
  const sig = await signHashWithFounderKey(await sha256hex(canonical));
  const token = await getAccessToken();
  const resp = await fetch(`${apiBaseUrl()}/admin/marketing/campaigns/bulk-reject`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Founder-Signature': sig,
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: canonical,
  });
  if (!resp.ok) {
    const err = (await resp.json().catch(() => ({}))) as { detail?: string };
    throw new Error(err.detail ?? `HTTP ${resp.status}`);
  }
  const data = (await resp.json()) as { rejected_count: number };
  return data.rejected_count;
}

/** Default reason for the "Reject All Pending" backlog-clear (Founder 2026-06-22). */
const REJECT_ALL_REASON =
  'Generic content, not pain-targeted. Regenerate after pain_points integration.';

/** Founder-signed reject of EVERY non-terminal campaign across all products
 *  (pending / running / approved). Returns the count rejected. */
async function rejectAllPending(reason: string): Promise<number> {
  const body = { nonce: crypto.randomUUID(), reason };
  const canonical = JSON.stringify(sortKeys(body));
  const sig = await signHashWithFounderKey(await sha256hex(canonical));
  const token = await getAccessToken();
  const resp = await fetch(`${apiBaseUrl()}/admin/marketing/campaigns/reject-all-pending`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Founder-Signature': sig,
      // Anti-injection phrase required by the Founder-gated mutation pattern
      // (require_founder / check_omega_phrase). Harmless if the route verifies
      // the body signature directly; required if it is behind require_founder.
      'X-Omega-Confirm': 'GI-OMEGA-SHALEV-2026',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: canonical,
  });
  if (!resp.ok) {
    const err = (await resp.json().catch(() => ({}))) as { detail?: string };
    throw new Error(err.detail ?? `HTTP ${resp.status}`);
  }
  const data = (await resp.json()) as { rejected_count: number };
  return data.rejected_count;
}

// ── Types ────────────────────────────────────────────────────────────────────

interface CampaignSummary {
  id: string;
  product_id: string;
  status: string;
  omega_log_seq: number;
  created_at: number;
  executed_at: number | null;
  slug: string | null;
  title_tag: string | null;
}

interface ListCampaignsResponse {
  campaigns: CampaignSummary[];
  total: number;
}

type SortField = 'created_at' | 'executed_at' | 'status' | 'title_tag';
type SortDir = 'asc' | 'desc';

// ── Data ─────────────────────────────────────────────────────────────────────

function fetchCampaigns(): Promise<ListCampaignsResponse> {
  return apiFetch<ListCampaignsResponse>('/admin/marketing/campaigns');
}

// ── Constants ────────────────────────────────────────────────────────────────

const STATUS_COLORS: Record<string, string> = {
  pending:   '#7dd3fc',
  running:   '#a78bfa',
  approved:  '#6ee7b7',
  published: '#34d399',
  rejected:  '#f87171',
  failed:    '#f87171',
};

const ALL_STATUSES = ['pending', 'running', 'approved', 'published', 'rejected', 'failed'];

// ── Helpers ──────────────────────────────────────────────────────────────────

function fmtTime(unix: number): string {
  return new Date(unix * 1000).toLocaleString('en-US', {
    month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
  });
}

function parseLocalDate(s: string): number | null {
  const d = new Date(s);
  return isNaN(d.getTime()) ? null : d.getTime() / 1000;
}

function sortCampaigns(list: CampaignSummary[], field: SortField, dir: SortDir): CampaignSummary[] {
  return [...list].sort((a, b) => {
    let va: string | number, vb: string | number;
    switch (field) {
      case 'created_at':  va = a.created_at;          vb = b.created_at;          break;
      case 'executed_at': va = a.executed_at ?? 0;     vb = b.executed_at ?? 0;    break;
      case 'status':      va = a.status;               vb = b.status;              break;
      case 'title_tag':   va = a.title_tag ?? a.slug ?? a.product_id;
                          vb = b.title_tag ?? b.slug ?? b.product_id; break;
    }
    if (va < vb) return dir === 'asc' ? -1 : 1;
    if (va > vb) return dir === 'asc' ?  1 : -1;
    return 0;
  });
}

// ── Sub-components ────────────────────────────────────────────────────────────

const inputStyle: React.CSSProperties = {
  background: 'rgba(255,255,255,0.06)',
  border: '1px solid var(--omega-rim, rgba(255,255,255,0.12))',
  borderRadius: 4,
  color: 'var(--omega-ink)',
  fontFamily: 'var(--omega-mono)',
  fontSize: '8.5pt',
  padding: '4px 8px',
  outline: 'none',
};

function SortHeader({
  label, field, active, dir, onSort,
}: {
  label: string;
  field: SortField;
  active: boolean;
  dir: SortDir;
  onSort: (f: SortField) => void;
}) {
  return (
    <button
      onClick={() => onSort(field)}
      style={{
        background: 'none',
        border: 'none',
        padding: 0,
        cursor: 'pointer',
        fontFamily: 'var(--omega-mono)',
        fontSize: '8.5pt',
        fontWeight: 700,
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
        color: active ? '#7dd3fc' : 'var(--omega-ink-subtle)',
      }}
    >
      {label}{active ? (dir === 'asc' ? ' ↑' : ' ↓') : ''}
    </button>
  );
}

// ── Page ─────────────────────────────────────────────────────────────────────

export function MarketingCampaignsPage() {
  if (!isOmegaAdmin()) return <Navigate to="/" replace />;

  const [statusFilter, setStatusFilter] = useState('');
  const [search, setSearch]             = useState('');
  const [dateFrom, setDateFrom]         = useState('');
  const [dateTo, setDateTo]             = useState('');
  const [sortField, setSortField]       = useState<SortField>('created_at');
  const [sortDir, setSortDir]           = useState<SortDir>('desc');
  const [bulkSlug, setBulkSlug]         = useState('');
  const [bulkBusy, setBulkBusy]         = useState(false);
  const [bulkMsg, setBulkMsg]           = useState<string | null>(null);

  const { data, isPending, isError } = useQuery({
    queryKey: ['marketing', 'campaigns'],
    queryFn: fetchCampaigns,
    refetchInterval: 30_000,
  });

  const all = data?.campaigns ?? [];

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const from = parseLocalDate(dateFrom);
    const to   = parseLocalDate(dateTo ? dateTo + 'T23:59:59' : '');

    let list = all;
    if (statusFilter) list = list.filter(c => c.status === statusFilter);
    if (q) {
      list = list.filter(c =>
        (c.title_tag ?? '').toLowerCase().includes(q) ||
        (c.product_id ?? '').toLowerCase().includes(q) ||
        (c.slug ?? '').toLowerCase().includes(q),
      );
    }
    if (from !== null) list = list.filter(c => c.created_at >= from);
    if (to   !== null) list = list.filter(c => c.created_at <= to);
    return sortCampaigns(list, sortField, sortDir);
  }, [all, statusFilter, search, dateFrom, dateTo, sortField, sortDir]);

  function handleSort(field: SortField) {
    if (field === sortField) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('desc'); }
  }

  async function handleBulkReject() {
    const slug = bulkSlug.trim();
    if (!slug) return;
    const approvedForSlug = all.filter(c => c.slug === slug && c.status === 'approved').length;
    // Mandatory confirm — never one-click destructive (QC-003).
    if (!window.confirm(
      `Reject ALL approved campaigns for product "${slug}"` +
      `${approvedForSlug > 0 ? ` (${approvedForSlug} match the loaded list)` : ''}? This sets them to rejected.`,
    )) return;
    setBulkBusy(true);
    setBulkMsg(null);
    try {
      const count = await bulkRejectBySlug(slug);
      setBulkMsg(`Rejected ${count} campaign${count === 1 ? '' : 's'} for "${slug}".`);
      setBulkSlug('');
    } catch (e) {
      setBulkMsg(e instanceof Error ? `Error: ${e.message}` : 'Bulk-reject failed.');
    } finally {
      setBulkBusy(false);
    }
  }

  async function handleRejectAllPending() {
    const pending = all.filter(c => c.status !== 'rejected' && c.status !== 'published').length;
    // Mandatory confirm — never one-click destructive (QC-003).
    if (!window.confirm(
      `Reject ALL ${pending} non-terminal campaign${pending === 1 ? '' : 's'} (pending / running / approved) ` +
      `across every product? This sets them to rejected.\n\nReason logged: "${REJECT_ALL_REASON}"`,
    )) return;
    setBulkBusy(true);
    setBulkMsg(null);
    try {
      const count = await rejectAllPending(REJECT_ALL_REASON);
      setBulkMsg(`Rejected ${count} campaign${count === 1 ? '' : 's'} (all products).`);
    } catch (e) {
      setBulkMsg(e instanceof Error ? `Error: ${e.message}` : 'Reject-all failed.');
    } finally {
      setBulkBusy(false);
    }
  }

  return (
    <div className="omega-shell" style={{ fontFamily: 'var(--omega-mono)', color: 'var(--omega-ink)', padding: '16px 20px' }}>
      <Breadcrumbs
        crumbs={[
          { label: 'OMEGA', to: '/omega' },
          { label: 'Marketing', to: '/omega/marketing' },
          { label: 'Campaigns' },
        ]}
      />

      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14, marginTop: 8 }}>
        <h1 style={{ margin: 0, fontSize: '11pt', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
          Campaigns
        </h1>
        {data && (
          <span style={{ background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 3, fontSize: '8pt', padding: '1px 7px', color: 'var(--omega-ink-subtle)' }}>
            {filtered.length}{filtered.length !== all.length ? `/${all.length}` : ''}
          </span>
        )}
      </div>

      {/* E3 / QC-003 — founder bulk-reject by product slug (mandatory confirm) */}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 12 }}>
        <input
          placeholder="Product slug…"
          value={bulkSlug}
          onChange={e => setBulkSlug(e.target.value)}
          style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 4, color: 'var(--omega-ink)', fontFamily: 'var(--omega-mono)', fontSize: '8.5pt', padding: '4px 8px' }}
        />
        <button
          onClick={handleBulkReject}
          disabled={bulkBusy || !bulkSlug.trim()}
          style={{ padding: '4px 12px', background: 'rgba(248,113,113,0.12)', color: '#f87171', border: '1px solid rgba(248,113,113,0.4)', borderRadius: 4, fontSize: '8.5pt', fontWeight: 600, cursor: bulkBusy || !bulkSlug.trim() ? 'default' : 'pointer' }}
        >
          {bulkBusy ? 'Rejecting…' : 'Reject all for product'}
        </button>
        <button
          onClick={handleRejectAllPending}
          disabled={bulkBusy}
          title="Reject every non-terminal campaign (pending / running / approved) across all products"
          style={{ padding: '4px 12px', background: 'rgba(248,113,113,0.18)', color: '#f87171', border: '1px solid rgba(248,113,113,0.55)', borderRadius: 4, fontSize: '8.5pt', fontWeight: 700, cursor: bulkBusy ? 'default' : 'pointer' }}
        >
          {bulkBusy ? 'Rejecting…' : 'Reject All Pending'}
        </button>
        {bulkMsg && <span style={{ fontSize: '8.5pt', color: bulkMsg.startsWith('Error') ? '#f87171' : '#6ee7b7' }}>{bulkMsg}</span>}
      </div>

      {/* Filter bar */}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16, alignItems: 'center' }}>
        <input
          placeholder="Search title / product / slug…"
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{ ...inputStyle, minWidth: 200, flex: '1 1 200px' }}
        />
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} style={inputStyle}>
          <option value="">All statuses</option>
          {ALL_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <input
          type="date"
          value={dateFrom}
          onChange={e => setDateFrom(e.target.value)}
          title="From date"
          style={inputStyle}
        />
        <span style={{ fontSize: '8.5pt', color: 'var(--omega-ink-subtle)' }}>→</span>
        <input
          type="date"
          value={dateTo}
          onChange={e => setDateTo(e.target.value)}
          title="To date"
          style={inputStyle}
        />
        {(statusFilter || search || dateFrom || dateTo) && (
          <button
            onClick={() => { setStatusFilter(''); setSearch(''); setDateFrom(''); setDateTo(''); }}
            style={{ ...inputStyle, cursor: 'pointer', color: '#f87171', border: '1px solid rgba(248,113,113,0.3)' }}
          >
            Clear
          </button>
        )}
      </div>

      {/* Column headers */}
      {!isPending && !isError && filtered.length > 0 && (
        <div style={{
          display: 'grid',
          gridTemplateColumns: '90px 1fr 130px 120px',
          gap: 12,
          padding: '6px 12px',
          borderBottom: '1px solid var(--omega-rim, rgba(255,255,255,0.1))',
          marginBottom: 2,
        }}>
          <SortHeader label="Status"   field="status"      active={sortField === 'status'}      dir={sortDir} onSort={handleSort} />
          <SortHeader label="Campaign" field="title_tag"   active={sortField === 'title_tag'}   dir={sortDir} onSort={handleSort} />
          <SortHeader label="Executed" field="executed_at" active={sortField === 'executed_at'} dir={sortDir} onSort={handleSort} />
          <SortHeader label="Created"  field="created_at"  active={sortField === 'created_at'}  dir={sortDir} onSort={handleSort} />
        </div>
      )}

      {isPending && <div style={{ color: 'var(--omega-ink-subtle)', fontSize: '9pt' }}>Loading…</div>}
      {isError   && <div style={{ color: '#f87171', fontSize: '9pt' }}>Failed to load campaigns.</div>}

      {!isPending && !isError && filtered.length === 0 && (
        <div style={{ color: 'var(--omega-ink-subtle)', fontSize: '9pt', fontStyle: 'italic', padding: '40px 0', textAlign: 'center' }}>
          No campaigns match the current filters.
        </div>
      )}

      {filtered.map(c => (
        <Link
          key={c.id}
          to={`/omega/marketing/campaigns/${c.id}`}
          style={{
            display: 'grid',
            gridTemplateColumns: '90px 1fr 130px 120px',
            gap: 12,
            alignItems: 'center',
            padding: '10px 12px',
            borderTop: '1px solid var(--omega-rim, rgba(255,255,255,0.06))',
            textDecoration: 'none',
            color: 'inherit',
          }}
          onMouseEnter={e => { (e.currentTarget as HTMLAnchorElement).style.background = 'rgba(255,255,255,0.03)'; }}
          onMouseLeave={e => { (e.currentTarget as HTMLAnchorElement).style.background = 'transparent'; }}
        >
          <span style={{
            fontSize: '7.5pt',
            padding: '1px 6px',
            border: `1px solid ${STATUS_COLORS[c.status] ?? 'var(--omega-ink-subtle)'}`,
            color: STATUS_COLORS[c.status] ?? 'var(--omega-ink-subtle)',
            letterSpacing: '0.06em',
            textTransform: 'uppercase',
            fontWeight: 700,
            borderRadius: 3,
            justifySelf: 'start',
          }}>
            {c.status}
          </span>

          <div style={{ overflow: 'hidden' }}>
            <div style={{ fontSize: '10pt', fontWeight: 600, color: 'var(--omega-ink)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {c.title_tag ?? c.slug ?? c.product_id}
            </div>
            {c.slug && (
              <div style={{ fontSize: '8.5pt', color: 'var(--omega-ink-subtle)', marginTop: 1 }}>
                /{c.slug}
              </div>
            )}
          </div>

          <span style={{ fontSize: '8.5pt', color: 'var(--omega-ink-subtle)' }}>
            {c.executed_at ? fmtTime(c.executed_at) : '—'}
          </span>

          <span style={{ fontSize: '8.5pt', color: 'var(--omega-ink-subtle)' }}>
            {fmtTime(c.created_at)}
          </span>
        </Link>
      ))}
    </div>
  );
}
